import { isAxiosError } from 'axios'

type ErrorBody = {
  error?: string
  Error?: string
  message?: string
  title?: string
  detail?: string
  errors?: Record<string, string[]>
}

function isGatewayStatus(status: number | undefined): boolean {
  return status === 502 || status === 503 || status === 504
}

function looksLikeHtml(value: string): boolean {
  const trimmed = value.trimStart().slice(0, 200).toLowerCase()
  return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html') || trimmed.includes('<title>')
}

function isArrayBufferData(data: unknown): data is ArrayBuffer {
  return (
    data instanceof ArrayBuffer ||
    Object.prototype.toString.call(data) === '[object ArrayBuffer]'
  )
}

function decodeArrayBufferMessage(data: ArrayBuffer): string | null {
  try {
    const text = new TextDecoder().decode(data).trim()
    if (!text || looksLikeHtml(text)) return null
    if (text.startsWith('{')) {
      const parsed = JSON.parse(text) as ErrorBody
      return messageFromErrorObject(parsed)
    }
    return text.slice(0, 300)
  } catch {
    return null
  }
}

function messageFromErrorObject(data: ErrorBody): string | null {
  const apiError = data.error?.trim() || data.Error?.trim()
  if (apiError) return apiError
  if (data.message?.trim()) return data.message
  if (data.title?.trim()) return data.detail ? `${data.title}: ${data.detail}` : data.title
  if (data.errors) {
    const parts = Object.values(data.errors).flat().filter(Boolean)
    if (parts.length > 0) return parts.join(' ')
  }
  return null
}

function messageFromErrorBody(data: unknown): string | null {
  if (isArrayBufferData(data)) {
    return decodeArrayBufferMessage(data)
  }
  if (typeof data === 'string' && data.trim()) {
    return looksLikeHtml(data) ? null : data
  }
  if (data && typeof data === 'object') {
    return messageFromErrorObject(data as ErrorBody)
  }
  return null
}

function isHtmlErrorBody(data: unknown): boolean {
  if (typeof data === 'string') return looksLikeHtml(data)
  if (isArrayBufferData(data)) {
    try {
      return looksLikeHtml(new TextDecoder().decode(data))
    } catch {
      return false
    }
  }
  return false
}

export function httpErrorStatus(error: unknown): number | undefined {
  if (!isAxiosError(error)) return undefined
  const status = error.response?.status
  return typeof status === 'number' ? status : undefined
}

export function isRateLimitedError(error: unknown): boolean {
  return httpErrorStatus(error) === 429
}

/** Turn axios / API failures into a user-readable message. */
export function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    if (isGatewayStatus(error.response?.status)) {
      return 'The workspace server is temporarily unavailable. Try again in a moment.'
    }
    if (error.response?.status === 413) {
      return 'File is too large for the server upload limit. Try a smaller ZIP or upload files one at a time.'
    }
    const fromBody = messageFromErrorBody(error.response?.data)
    if (error.response?.status === 404) {
      return (
        fromBody ??
        'That download is not available on this server yet. Refresh or try again after the API update is live.'
      )
    }
    if (error.response?.status === 403) {
      if (fromBody && !/^forbidden$/i.test(fromBody.trim())) {
        return fromBody
      }
      return 'You do not have permission for this action.'
    }
    if (error.response?.status === 429) {
      return fromBody ?? 'Too many requests. Please wait a moment and try again.'
    }
    if (!error.response) {
      const msg = (error.message ?? '').toLowerCase()
      if (msg.includes('network error') || msg.includes('failed')) {
        const isDownload = error.config?.responseType === 'arraybuffer' || error.config?.responseType === 'blob'
        return isDownload
          ? 'Could not reach the server. Check your connection and try again. If this happens on a download, the API may be unreachable or blocked by the gateway.'
          : 'Could not reach the server. Check your connection and try again.'
      }
    }
    if (fromBody) return fromBody
    if (isHtmlErrorBody(error.response?.data)) return fallback
    if (error.message) return error.message
  }
  if (error instanceof Error && error.message) return error.message
  return fallback
}
